// /opt/isp_erp/frontend/src/utils/imageCompressor.js

/**
 * Comprime una imagen antes de enviarla al servidor
 * @param {File} file - Archivo de imagen
 * @param {number} maxWidth - Ancho máximo (default 1024px)
 * @param {number} quality - Calidad (0.1 a 1.0, default 0.7)
 * @returns {Promise<string>} - Base64 comprimido
 */
export const compressImage = (file, maxWidth = 1024, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    // Verificar si es imagen
    if (!file.type.startsWith('image/')) {
      reject(new Error('El archivo no es una imagen'));
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        // Calcular nuevo tamaño manteniendo proporción
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Crear canvas para comprimir
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a base64 con calidad reducida
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);

        // Verificar tamaño final (máximo 1MB por foto)
        const sizeInBytes = Math.ceil(compressedBase64.length * 0.75);
        const sizeInMB = sizeInBytes / (1024 * 1024);

        if (sizeInMB > 1.5) {
          // Si aún es muy grande, comprimir más
          const newQuality = quality * 0.7;
          const moreCompressed = canvas.toDataURL('image/jpeg', newQuality);
          resolve(moreCompressed);
        } else {
          resolve(compressedBase64);
        }

        // Limpiar memoria
        URL.revokeObjectURL(img.src);
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };
  });
};

/**
 * Comprime múltiples imágenes
 * @param {Array} files - Lista de archivos
 * @param {number} maxWidth - Ancho máximo
 * @param {number} quality - Calidad
 * @returns {Promise<Array>} - Lista de base64 comprimidos
 */
export const compressMultipleImages = async (files, maxWidth = 1024, quality = 0.7) => {
  const compressed = [];
  for (const file of files) {
    try {
      const compressedBase64 = await compressImage(file, maxWidth, quality);
      compressed.push(compressedBase64);
    } catch (error) {
      console.error('Error comprimiendo imagen:', error);
    }
  }
  return compressed;
};

/**
 * Validar tamaño total de las fotos
 * @param {Array} base64Array - Lista de base64
 * @returns {boolean} - Si excede el límite
 */
export const validateTotalSize = (base64Array, maxTotalMB = 5) => {
  let totalBytes = 0;
  for (const base64 of base64Array) {
    // Aproximación del tamaño en bytes
    const sizeInBytes = Math.ceil(base64.length * 0.75);
    totalBytes += sizeInBytes;
  }
  const totalMB = totalBytes / (1024 * 1024);
  return totalMB <= maxTotalMB;
};
